import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/authContext';
import { Button, Input } from "../components/ui";

export const AdminUserRequestsPage = () => {
    const { user, token, role } = useContext(AuthContext);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [filter, setFilter] = useState('pendiente');
    const isProvincial = role === 'editor_provincial';

    useEffect(() => {
        fetchRequests();
    }, [filter]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/user-requests/requests?status=${filter}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) throw new Error('Error al cargar solicitudes');
            
            const data = await response.json();
            setRequests(data);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        if (!confirm('¿Está seguro de aprobar esta solicitud? Se enviará un email con el enlace de registro.')) {
            return;
        }

        try {
            const response = await fetch(`/api/user-requests/requests/${id}/approve`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Error al aprobar solicitud');

            setMessage('Solicitud aprobada correctamente. Se ha enviado el email de registro.');
            fetchRequests();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const handleReject = async (id) => {
        if (!confirm('¿Está seguro de rechazar esta solicitud?')) {
            return;
        }

        try {
            const response = await fetch(`/api/user-requests/requests/${id}/reject`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Error al rechazar solicitud');

            setMessage('Solicitud rechazada correctamente.');
            fetchRequests();
        } catch (error) {
            setMessage(error.message);
        }
    };

    const handleDownloadPdf = async (id, filename) => {
        try {
            const response = await fetch(`/api/user-requests/requests/${id}/pdf`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('No autorizado. Inicie sesión nuevamente.');
                }
                if (response.status === 404) {
                    throw new Error('PDF no encontrado.');
                }
                throw new Error('Error al descargar el PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename || 'nota.pdf';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            setMessage(error.message);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pendiente: 'bg-yellow-100 text-yellow-800',
            aprobado: 'bg-green-100 text-green-800',
            rechazado: 'bg-red-100 text-red-800',
            completado: 'bg-blue-100 text-blue-800'
        };
        return styles[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
                    Gestión de Solicitudes de Usuario
                </h1>

                {message && (
                    <div className={`mb-4 rounded-md p-4 ${message.includes('Error') ? 'bg-red-50' : 'bg-green-50'}`}>
                        <p className={`text-sm font-medium ${message.includes('Error') ? 'text-red-800' : 'text-green-800'}`}>
                            {message}
                        </p>
                    </div>
                )}

                {/* Filter tabs */}
                <div className="mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {['pendiente', 'aprobado', 'rechazado', 'completado'].map((status) => (
                                <button
                                    key={status}
                                    onClick={() => setFilter(status)}
                                    className={`
                                        py-2 px-1 border-b-2 font-medium text-sm capitalize
                                        ${filter === status
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    {status === 'pendiente' && 'Pendientes'}
                                    {status === 'aprobado' && 'Aprobadas'}
                                    {status === 'rechazado' && 'Rechazadas'}
                                    {status === 'completado' && 'Completadas'}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Cargando solicitudes...</p>
                    </div>
                ) : requests.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                        <p className="text-gray-500">No hay solicitudes {filter}s.</p>
                    </div>
                ) : (
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                        <ul className="divide-y divide-gray-200">
                            {requests.map((request) => (
                                <li key={request.id} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {request.nombre_municipio}
                                                    </p>
                                                    <p className="text-sm text-gray-500">
                                                        Código: {request.codigo_partido}
                                                    </p>
                                                </div>
                                                <span className={`ml-4 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(request.status)}`}>
                                                    {request.status}
                                                </span>
                                                <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    request.solicited_role === 'visualizador_municipio'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                    {request.solicited_role === 'visualizador_municipio' ? 'Solo visualización' : 'Editor'}
                                                </span>
                                            </div>
                                            
                                            <div className="mt-2 text-sm text-gray-500">
                                                    {request.solicited_role !== 'visualizador_municipio' && (
                                                        <p><strong>Intendente:</strong> {request.nombre_intendente}</p>
                                                    )}
                                                    <p><strong>Responsable:</strong> {request.nombre_responsable}</p>
                                                    {request.organizacion && (
                                                        <p><strong>Organización:</strong> {request.organizacion}</p>
                                                    )}
                                                    <p><strong>Email:</strong> {request.email_responsable}</p>
                                                    <p><strong>Solicitud:</strong> {new Date(request.created_at).toLocaleString('es-AR')}</p>
                                                {request.nota_pdf_path && (
                                                    <p>
                                                        <strong>Nota PDF:</strong>{' '}
                                                        <button 
                                                            onClick={() => handleDownloadPdf(request.id, request.nota_pdf_name || 'nota.pdf')}
                                                            className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
                                                        >
                                                            Ver documento
                                                        </button>
                                                    </p>
                                                )}
                                                {request.token_expires_at && (
                                                    <p><strong>Token expira:</strong> {new Date(request.token_expires_at).toLocaleString('es-AR')}</p>
                                                )}
                                            </div>
                                        </div>

                                        {request.status === 'pendiente' && (
                                            <div className="ml-4 flex-shrink-0 flex space-x-2">
                                                <Button
                                                    onClick={() => handleApprove(request.id)}
                                                    variant="success"
                                                    className="text-sm"
                                                >
                                                    Aprobar
                                                </Button>
                                                <Button
                                                    onClick={() => handleReject(request.id)}
                                                    variant="danger"
                                                    className="text-sm"
                                                >
                                                    Rechazar
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminUserRequestsPage;
